import {
  Box,
  Container,
  Flex,
  Button,
  Icon,
  Text,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import {
  AddListingBanner,
  Breadcrumb,
  CategoryCards,
  ErrorMessageBox,
  SponsorsCard,
  LoginPrompt,
  CoverLoader,
  SampleListingsBanner,
} from 'src/components/atoms';
import { HeroCard, Announcement } from 'src/components/molecules';
import { ListingAttributes, ListingsView, FeaturedListingsBar } from 'src/components/organisms';
import { Category, MarketplaceInfo, PaginatedListingsResponse, SiteAndId } from 'src/api/v1-api';
import { CONTAINER_MAX_WIDTH_1, CONTAINER_MAX_WIDTH_2, PAGE_SECTION_GAP } from 'src/constants/ui';
import { useEffect, useState } from 'react';
import { AttributeSelectorProvider } from 'src/contexts/AttributeSelector';
import { useRouter } from 'next/router';
import { FiPlusCircle } from 'react-icons/fi';
import { useAuth } from 'src/utils/auth';
import { GetStaticPropsContext } from 'next';
import CloneCategoryMenuButton from 'src/components/molecules/admin/clone-category-menu-button';
import { NextSeo } from 'next-seo';
import { checkCategoryPermissions } from 'src/utils/categoryUtils';
import { srcFromCDN } from 'src/utils/files';
import { LISTINGS_PER_PAGE, MAP_LISTINGS_PER_PAGE } from 'src/constants/marketplace';
import { getMarketplaceUrl } from 'src/utils/marketplaceUtils';
import CollectEmailsModule from 'src/components/atoms/collect-emails-module';
interface Props {
  category: Category;
  marketplace: MarketplaceInfo;
  listings: PaginatedListingsResponse;
}

const ViewCategory = ({ category, marketplace, listings }: Props) => {
  const router = useRouter();
  const { user, hasRoles, userLoaded, hasAdmin, hasStaff } = useAuth();

  const isAdmin = hasAdmin(marketplace?.id);
  const isStaffUser = hasStaff();
  const [announcementOpened, setAnnouncementOpened] = useState(true);
  const [userChangedPriceRange, handleUserChangedPriceRange] = useState(false);
  const showAnnouncement = category?.functional?.announcement && announcementOpened;
  const [hasPermission, setHasPermission] = useState<boolean>(true);
  // Initialize permissionChecked to true if category has no role requirements (public category)
  const [permissonChecked, setPermissionChecked] = useState<boolean>(
    !category?.view_listing_roles || category.view_listing_roles.length === 0,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>('');

  const showAddListing = category?.functional?.show_create_listing_button;

  // Workaround for silencing _AutofillCallbackHandler error in Instagram browser
  useEffect(() => {
    // @ts-ignore
    window._AutofillCallbackHandler =
      // @ts-ignore
      window._AutofillCallbackHandler ||
      function () {
        // This function intentionally left blank
      };
  }, []);

  const inventoryDisplay = category?.functional?.inventory_display;

  useEffect(() => {
    const checkPermissions = async () => {
      // Fast path: If category has no view listing roles, everyone can view (no API call needed)
      if (!category?.view_listing_roles || category.view_listing_roles.length === 0) {
        setHasPermission(true);
        setErrorMessage(null);
        setPermissionChecked(true);
        return;
      }

      // Slow path: Category has role requirements, verify user has required roles
      const { hasPermission, errorMessage } = await checkCategoryPermissions(
        category?.id,
        marketplace?.id,
        user,
        hasRoles,
      );

      setHasPermission(isAdmin || isStaffUser || hasPermission);
      setErrorMessage(errorMessage);
      setPermissionChecked(true);
    };

    if (userLoaded) {
      checkPermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category?.id, marketplace?.id, user, userLoaded]);

  const restoreScroll = (url: string) => {
    const savedY = sessionStorage.getItem(`scrollPos:${url}`);
    if (savedY) {
      window.scrollTo(0, parseFloat(savedY));
    }
  };
  const [listingsViewMounted, setListingsViewMounted] = useState(false);
  useEffect(() => {
    if (listingsViewMounted) {
      restoreScroll(router.asPath);
    }
  }, [listingsViewMounted, router.asPath]);

  const marketplaceUrl = getMarketplaceUrl(marketplace);
  const categoryUrl = `${marketplaceUrl}/category/${category?.id}`;
  const categoryImage = category?.image ? srcFromCDN(category.image, 800) : '';

  if (!marketplace || !category) {
    return null;
  }

  return (
    <>
      <NextSeo
        title={`${category?.name} | ${marketplace?.name}`}
        description={category?.description ?? ''}
        canonical={categoryUrl}
        openGraph={{
          type: 'website',
          url: categoryUrl,
          title: category?.name,
          description: category?.description ?? '',
          images: [
            {
              url: categoryImage,
              width: 800,
              height: 600,
              alt: category?.name,
            },
          ],
          site_name: marketplace?.name ?? category?.name,
          siteName: marketplace?.name ?? category?.name,
        }}
        twitter={{
          site: categoryUrl,
          cardType: 'summary_large_image',
        }}
      />

      {router.isFallback || !userLoaded || !permissonChecked ? (
        <CoverLoader py={8} context="CategoryPage:CheckingPermissions" />
      ) : (
        <>
          {hasPermission ? (
            <>
              {errorMessage && (
                <Flex mb={{ base: 2, md: 4, lg: 6 }} justify="center">
                  <Alert status="warning" borderRadius="lg">
                    <AlertIcon />
                    {errorMessage}
                  </Alert>
                </Flex>
              )}
              <AddListingBanner page="category" />
              {marketplace?.functional?.collect_email_module?.position === 'top' && (
                <CollectEmailsModule
                  page="category"
                  collect_email_module={marketplace?.functional?.collect_email_module}
                  marketplace={marketplace}
                />
              )}
              {showAnnouncement && (
                <Container maxW={CONTAINER_MAX_WIDTH_1} pt={{ base: '8px', lg: '12px' }}>
                  <Announcement
                    text={category?.functional?.announcement ?? ''}
                    onClose={() => setAnnouncementOpened(false)}
                  />
                </Container>
              )}
              <SponsorsCard category={category} />
              <Container
                maxW={CONTAINER_MAX_WIDTH_1}
                pt={{ base: 4, md: 4, lg: 6 }}
                display="flex"
                flexDirection="column"
              >
                <Flex w="full" direction="column" gap={PAGE_SECTION_GAP}>
                  {router.isFallback && (
                    <CoverLoader size="xl" context="CategoryPage:LoadingCategory" />
                  )}

                  <CloneCategoryMenuButton categoryId={category?.id} />

                  {category &&
                    category?.functional &&
                    category?.functional?.hero_card &&
                    (category?.functional?.hero_card.hero_card_at_top ?? true) &&
                    Object.keys(category?.functional?.hero_card).length > 0 && (
                      <Box>
                        <HeroCard
                          title={category.functional.hero_card.hero_card_title}
                          description={category.functional.hero_card.hero_card_description}
                          imageURL={category.functional.hero_card.hero_card_image_url}
                          mobileImageURL={category.functional.hero_card.hero_card_mobile_image_url}
                          cta={{
                            label: category.functional.hero_card.hero_card_cta,
                            link: category.functional.hero_card.hero_card_link,
                          }}
                          text_box_opacity={category.functional.opacity}
                          isSimple={category.functional.hero_card?.hero_card_is_simple}
                        />
                      </Box>
                    )}

                  <Flex justifyContent="flex-start" alignItems="center" w="full">
                    <Breadcrumb parent={category?.parent} current={category?.name} />
                    <Box>
                      {showAddListing && (
                        <Button
                          colorScheme="primary"
                          onClick={() => router.push(`/listing/create?category=${category?.id}`)}
                        >
                          <Icon as={FiPlusCircle} mr="2" />
                          {category?.functional?.text_overrides?.add_listing_btn || 'Add Listing'}
                        </Button>
                      )}
                    </Box>
                  </Flex>

                  <CategoryCards category={category} />

                  <Flex direction="column" gap={PAGE_SECTION_GAP}>
                    <AttributeSelectorProvider
                      attributes={category?.attributes ?? []}
                      useToggle={(category?.functional?.enabled_view_types ?? []).length > 1}
                      defaultViewType={category?.functional?.default_view_type}
                      maxPrice={
                        category?.functional?.filter_max_price ??
                        category?.functional?.computed_filter_max_price
                      }
                      sortOptions={category?.functional?.sort_options ?? []}
                      categoryInFilters={category}
                    >
                      {category?.functional?.has_featured_bar && (
                        <FeaturedListingsBar category={category} />
                      )}
                      {inventoryDisplay && inventoryDisplay !== 'None' && (
                        <ListingAttributes
                          category={category}
                          handleUserChangedPriceRange={handleUserChangedPriceRange}
                        />
                      )}
                      <SampleListingsBanner marketplace={marketplace} />
                      <ListingsView
                        propListings={listings}
                        fetchUrl={`${process.env.NEXT_PUBLIC_API_HOST}/listings/category/${category?.id}`}
                        initialMapFetchUrl={`${process.env.NEXT_PUBLIC_API_HOST}/listings/map/${category?.id}/`}
                        inventoryDisplay={category?.functional?.inventory_display}
                        paginationStyle={category?.functional?.pagination_style}
                        resultsPerPage={
                          category?.functional?.results_per_page
                            ? category.functional.results_per_page
                            : LISTINGS_PER_PAGE
                        }
                        mapResultsPerPage={
                          category?.functional?.map_results_per_page
                            ? category.functional.map_results_per_page
                            : MAP_LISTINGS_PER_PAGE
                        }
                        category={category}
                        userChangedPriceRange={userChangedPriceRange}
                        onMountComplete={() => setListingsViewMounted(true)}
                      />
                    </AttributeSelectorProvider>
                  </Flex>
                </Flex>
              </Container>
            </>
          ) : user ? (
            <Container maxW={CONTAINER_MAX_WIDTH_2} mt={10}>
              <ErrorMessageBox>
                {errorMessage}
                &nbsp;You can add roles to your account from the&nbsp;
                <Button variant="link" onClick={() => router.push('/account/roles')}>
                  <Text as="span" textDecoration="underline">
                    Marketplace Roles
                  </Text>
                </Button>
                &nbsp;page.
              </ErrorMessageBox>
            </Container>
          ) : userLoaded ? (
            <Container maxW={CONTAINER_MAX_WIDTH_1} mt={10}>
              <LoginPrompt message="to view this category." showWithSignup={true} />
            </Container>
          ) : (
            // Show loading state while user is being loaded
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
              <Spinner />
            </Box>
          )}
        </>
      )}
    </>
  );
};

export async function getStaticProps(context: GetStaticPropsContext) {
  const hostname = context.params?.site;
  const id = context.params?.id;

  if (!hostname || !id) {
    return {
      notFound: true,
      revalidate: 1,
    };
  }

  try {
    const categoryResponse = await fetch(
      `${process.env.NEXT_PUBLIC_API_HOST}/category_with_listings/${id}/`,
    );

    if (!categoryResponse.ok) {
      return {
        notFound: true,
        revalidate: 1,
      };
    }

    const categoryPayload = await categoryResponse.json();

    let category = {};
    for (const key in categoryPayload) {
      if (key !== 'marketplace' && key !== 'listings') {
        category = { ...category, [key]: categoryPayload[key] };
      }
    }

    const marketplace = categoryPayload['marketplace'];
    let listings = categoryPayload['listings'];
    if (listings === undefined) {
      listings = {};
    }
    const siteSlug = hostname.toString().toLowerCase().replace('www.', '').split('.')[0];
    const validHostnames = [];
    if (marketplace?.domain?.status === 'active' && marketplace?.domain?.domain) {
      validHostnames.push(
        marketplace?.domain?.domain.toLowerCase().replace('www.', '').split('.')[0],
      );
    }

    if (marketplace?.slug) {
      validHostnames.push(marketplace.slug.toLowerCase());
    }

    if (!validHostnames.includes(siteSlug)) {
      // If the category's marketplace slug does not match the site slug, return 404
      return {
        notFound: true,
        revalidate: 1,
      };
    }

    return {
      props: {
        category,
        marketplace,
        listings,
      },
      revalidate: 10,
    };
  } catch (error) {
    return {
      notFound: true,
      revalidate: 1,
    };
  }
}

export async function getStaticPaths() {
  const res = await fetch(`${process.env.NEXT_PUBLIC_API_HOST}/active_category_ids/`);
  const data = await res.json();

  const paths = data
    .filter(
      (item: SiteAndId) =>
        item.site !== null && item.site !== undefined && item.id !== null && item.id !== undefined,
    )
    .map((item: SiteAndId) => ({
      params: {
        site: data[item.site.toString()] ?? item.site.toString(),
        id: item.id.toString(),
      },
    }));

  return { paths, fallback: 'blocking' };
}

export default ViewCategory;
